import { useEffect, useRef, useState } from "react";

type RevealDir = "up" | "left" | "right" | "scale";

interface RevealProps {
  children: React.ReactNode;
  delay?: number;
  dir?: RevealDir;
  className?: string;
}

const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

/** Short, subtle scroll-in. Disabled entirely for reduced-motion users. */
export function Reveal({ children, delay = 0, dir = "up", className = "" }: RevealProps) {
  const ref = useRef<HTMLDivElement>(null);
  const [reduced] = useState(prefersReducedMotion);
  const [vis, setVis] = useState(false);

  useEffect(() => {
    if (reduced) return;
    const io = new IntersectionObserver(([e]) => {
      if (e?.isIntersecting) setVis(true);
    }, { threshold: 0.05 });
    const el = ref.current;
    if (el) io.observe(el);
    return () => io.disconnect();
  }, [reduced]);

  if (reduced) return <div className={className}>{children}</div>;

  const tx: Record<RevealDir, string> = {
    up: vis ? "translateY(0)" : "translateY(18px)",
    left: vis ? "translateX(0)" : "translateX(-18px)",
    right: vis ? "translateX(0)" : "translateX(18px)",
    scale: vis ? "scale(1)" : "scale(0.97)",
  };

  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: vis ? 1 : 0,
        transform: tx[dir],
        transition: `opacity 0.45s cubic-bezier(0.16,1,0.3,1) ${delay}ms,transform 0.45s cubic-bezier(0.16,1,0.3,1) ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}
